import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  HAQTable,
  HAQTableFilter,
} from "screens/Project/sections/HAQs/HAQs.types";
import { QueryAPIKey } from "utils/constants/api.constants";

export const TABLE_CONFIGURATION_KEY = "tableConfiguration";

export const DEFAULT_TABLE_CONFIGURATION = {
  HAQs: [
    { id: HAQTable.Question as const, visible: true },
    { id: HAQTable.Type as const, visible: false },
    { id: HAQTable.AuthoringOrganization as const, visible: true },
    { id: HAQTable.Domain as const, visible: false },
    { id: HAQTable.Topic as const, visible: false },
    { id: HAQTable.Status as const, visible: true },
    { id: HAQTable.SubmissionDetails as const, visible: true },
    { id: HAQTable.DueDate as const, visible: true },
    { id: HAQTable.NextAction as const, visible: true },
    { id: HAQTableFilter.StartDueDate as const, visible: true },
    { id: HAQTableFilter.EndDueDate as const, visible: true },
    { id: HAQTable.RowActions as const, visible: true },
  ],
};

export type TTableConfiguration = typeof DEFAULT_TABLE_CONFIGURATION;
export type TTableConfigurationId<T extends keyof TTableConfiguration> =
  TTableConfiguration[T][number]["id"];

export const useTableConfiguration = <T extends keyof TTableConfiguration>(
  table: T | undefined,
) => {
  const queryClient = useQueryClient();

  const { data: tableConfiguration, isLoading } = useQuery<TTableConfiguration>(
    {
      queryKey: [QueryAPIKey.GetTableConfiguration],
      queryFn: parseTableConfiguration,
    },
  );

  const allVisibleColumns: undefined | string[] =
    tableConfiguration && table && !isLoading
      ? tableConfiguration[table]
          .filter(({ visible }) => visible)
          .map(({ id }) => id)
      : undefined;

  const bulkSetColumnVisibility = (columnVisibility: {
    [K in TTableConfigurationId<T>]?: boolean;
  }) => {
    if (!tableConfiguration || !table) {
      return;
    }

    const newConfiguration = {
      ...tableConfiguration,
      [table]: tableConfiguration[table].map((columnConfiguration) => {
        const id: TTableConfigurationId<T> = columnConfiguration.id;

        if (id in columnVisibility) {
          return {
            ...columnConfiguration,
            visible: columnVisibility[id],
          };
        } else {
          return columnConfiguration;
        }
      }),
    };

    setTableConfiguration(newConfiguration);

    queryClient.setQueryData(
      [QueryAPIKey.GetTableConfiguration],
      newConfiguration,
    );
  };

  return {
    isTableConfigurationLoading: isLoading,
    tableConfiguration:
      tableConfiguration && table ? tableConfiguration?.[table] : undefined,
    allVisibleColumns,
    bulkSetColumnVisibility,
  };
};

export const parseTableConfiguration = (): TTableConfiguration => {
  const rawTableConfiguration = localStorage.getItem(TABLE_CONFIGURATION_KEY);

  const parsedTableConfiguration = rawTableConfiguration
    ? mergeDefaults(
        JSON.parse(rawTableConfiguration),
        DEFAULT_TABLE_CONFIGURATION,
      )
    : DEFAULT_TABLE_CONFIGURATION;

  setTableConfiguration(parsedTableConfiguration);

  return parsedTableConfiguration;
};

export const mergeDefaults = (
  configuration: TTableConfiguration,
  defaultConfiguration: TTableConfiguration,
) => {
  return Object.fromEntries(
    (Object.keys(configuration) as Array<keyof TTableConfiguration>).map(
      (tableKey) => {
        const defaultColumnsById = new Map(
          defaultConfiguration[tableKey].map((column) => [column.id, column]),
        );

        const configuredColumnIds = new Set(
          configuration[tableKey].map(({ id }) => id),
        );

        return [
          tableKey,
          [
            ...configuration[tableKey].map((column) => ({
              ...defaultColumnsById.get(column.id),
              ...column,
            })),
            ...defaultConfiguration[tableKey].filter(
              ({ id }) => !configuredColumnIds.has(id),
            ),
          ],
        ];
      },
    ),
  ) as TTableConfiguration;
};

export const setTableConfiguration = (configuration: TTableConfiguration) => {
  localStorage.setItem(TABLE_CONFIGURATION_KEY, JSON.stringify(configuration));
};
